import { fetchApi } from '@/services/api';

import type {
  ForecastAnomaliesPayload,
  ForecastChartSeriesPayload,
  ForecastInsightsPayload,
  ForecastProductSummariesPayload,
  ForecastRunRow,
  Paginated,
} from '@/modules/demand-planning/types/forecast.types';
import type { EntityScopeFilters } from '@/modules/demand-planning/components/ConsensusScopeFilters';
import type { ForecastChartHistoryRange } from '@/modules/demand-planning/types/forecast.types';
import type { ChartGranularity } from '@/modules/demand-planning/utils/chartLabels';
import { appendEntityScopeToRecord } from '@/modules/demand-planning/utils/entityScopeParams';

export const forecastService = {
  listRuns(): Promise<ForecastRunRow[]> {
    return fetchApi<Paginated<ForecastRunRow>>('demand-planning/forecast-runs/', {
      params: { ordering: '-started_at', page_size: 100 },
    }).then((data) => data.results ?? []);
  },

  getChartSeries(
    forecastRunId: number,
    options: {
      historyRange: ForecastChartHistoryRange;
      granularity: ChartGranularity;
      filters: EntityScopeFilters;
    },
  ): Promise<ForecastChartSeriesPayload> {
    const params: Record<string, string | string[]> = {
      forecast_run: String(forecastRunId),
      history_weeks: '52',
      history_range: options.historyRange,
      granularity: options.granularity,
    };
    appendEntityScopeToRecord(params, options.filters);
    return fetchApi<ForecastChartSeriesPayload>('demand-planning/forecast-lines/chart-series/', {
      params,
    });
  },

  getInsights(forecastRunId: number): Promise<ForecastInsightsPayload> {
    return fetchApi<ForecastInsightsPayload>(
      `demand-planning/forecast-runs/${forecastRunId}/insights/`,
    );
  },

  getAnomalies(forecastRunId: number, historyLimit = 30): Promise<ForecastAnomaliesPayload> {
    return fetchApi<ForecastAnomaliesPayload>(
      `demand-planning/forecast-runs/${forecastRunId}/anomalies/`,
      { params: { history_limit: String(historyLimit) } },
    );
  },

  applyAlertRecommendation(alertId: number, note?: string): Promise<unknown> {
    return fetchApi(`demand-planning/alerts/${alertId}/apply-recommendation/`, {
      method: 'POST',
      body: { note: note ?? '' },
    });
  },

  dismissAlert(alertId: number, note?: string): Promise<unknown> {
    return fetchApi(`demand-planning/alerts/${alertId}/dismiss/`, {
      method: 'POST',
      body: { note: note ?? '' },
    });
  },

  getProductSummaries(
    forecastRunId: number,
    options: {
      page: number;
      pageSize: number;
      trendWeeks?: number;
      filters: EntityScopeFilters;
    },
  ): Promise<ForecastProductSummariesPayload> {
    const params: Record<string, string | string[]> = {
      page: String(options.page),
      page_size: String(options.pageSize),
      trend_weeks: String(options.trendWeeks ?? 13),
    };
    appendEntityScopeToRecord(params, options.filters);
    return fetchApi<ForecastProductSummariesPayload>(
      `demand-planning/forecast-runs/${forecastRunId}/product-summaries/`,
      { params },
    );
  },
};
